// File: frontend/src/components/admin/UserDetail.js
//
// One user, everything we know about them.
//
// Replaces two near-duplicate views: the search modal's copy (which offered
// only the support-agent toggle) and the Users tab's copy. Having two meant
// what an admin could see and do depended on which door they came through.
import { useState, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Shield, Smartphone, LifeBuoy, Flame, AlertTriangle, Activity,
  CreditCard, Target, PauseCircle,
} from 'lucide-react';
import { adminFetch, useAdminSection } from './adminApi';
import {
  Card, Badge, Button, SectionError, EmptyState, Loading, StatGrid, StatCard,
  ScrollRegion, SeverityBadge, DefinitionList,
} from './primitives';
import { Sparkline, CHART_TOKENS } from './charts';
import {
  formatInstant, formatCalendarDate, formatRelative, formatRelativeDay, toMs, daysBetween,
} from '../../utils/adminDates';
import { logger } from '../../utils/logger';

const log = logger.child('adminUserDetail');

export function UserBadges({ user }) {
  return (
    <>
      {user.pro && <Badge tone="warning">Pro</Badge>}
      {user.support_agent && <Badge tone="info">Agent</Badge>}
      {user.is_admin && <Badge tone="danger">Admin</Badge>}
      {user.daily_overview_email && (
        <Badge tone="accent" title="Receives the daily overview email">Overview email</Badge>
      )}
      {!user.email_verified && <Badge tone="neutral">Unverified</Badge>}
    </>
  );
}

export function authLabel(user) {
  const methods = [];
  if (user.has_password) methods.push('Email');
  if (user.has_google) methods.push('Google');
  if (user.has_apple) methods.push('Apple');
  return methods.join(' + ') || '—';
}

function subscriptionSource(sub) {
  const parts = [];
  if (sub.pro_lifetime) parts.push('Lifetime purchase');
  if (sub.pro_expires_at && toMs(sub.pro_expires_at) > Date.now()) parts.push('Subscription');
  if (sub.pro_override) parts.push('Admin override');
  return parts.join(' + ') || '—';
}

// A flag toggle reads better as a labelled switch than as a button whose text
// flips between "Make admin" and "Revoke admin" — with the button you have to
// read the verb to work out the current state.
function FlagToggle({ label, description, on, onChange, busy, tone = 'accent' }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-divider last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink-main">{label}</div>
        {description && <div className="text-xs text-ink-light mt-0.5">{description}</div>}
      </div>
      <Button
        variant={on ? 'secondary' : 'ghost'}
        onClick={() => onChange(!on)}
        disabled={busy}
        aria-pressed={on}
        className={`shrink-0 px-3 min-h-[36px] text-xs ${on ? `text-${tone === 'accent' ? 'admin' : tone}` : ''}`}
      >
        {on ? 'On' : 'Off'}
      </Button>
    </div>
  );
}

export default function UserDetail({ apiUrl, userId, onBack, onAgentChange, onFlagsChange, onDeleted }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const section = useAdminSection(
    'user detail',
    () => `${apiUrl}/api/admin/users/${userId}`,
    [apiUrl, userId]
  );
  const detail = section.data;

  const mutate = useCallback(async (path, body, method = 'PUT') => {
    setError('');
    setBusy(true);
    try {
      await adminFetch(`${apiUrl}/api/admin/users/${userId}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      return true;
    } catch (err) {
      log.error('user mutation failed', err, { userId, path });
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [apiUrl, userId]);

  const toggleSupportAgent = async (value) => {
    if (await mutate('/support-agent', { support_agent: value })) {
      section.reload();
      onAgentChange?.(userId, value);
    }
  };

  const toggleFlag = async (flag, value) => {
    if (await mutate('/flags', { [flag]: value })) {
      section.reload();
      onFlagsChange?.(userId, { [flag]: value });
    }
  };

  const deleteUser = async () => {
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    if (await mutate('', null, 'DELETE')) onDeleted?.(userId);
    else setConfirmingDelete(false);
  };

  // 90 days of daily counts, zero-filled so a gap reads as a gap rather than
  // the line jumping straight across it.
  const sparkline = useMemo(() => {
    const counts = new Map((detail?.sparkline || []).map(p => [p.date, p.count]));
    const out = [];
    for (let i = 89; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0')].join('-');
      out.push({ date: key, count: counts.get(key) || 0 });
    }
    return out;
  }, [detail]);

  if (section.error) {
    return (
      <div>
        <Button variant="ghost" onClick={onBack} className="mb-4 px-0"><ArrowLeft size={14} /> Back to all users</Button>
        <SectionError message={`Couldn't load user ${userId}: ${section.error}`} onRetry={section.reload} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div>
        <Button variant="ghost" onClick={onBack} className="mb-4 px-0"><ArrowLeft size={14} /> Back to all users</Button>
        <Loading>Loading account…</Loading>
      </div>
    );
  }

  const { user, subscription, completionStats, habits, devices, securityEvents, support, sickWeeks, streaks, recentActivity } = detail;
  const accountAge = daysBetween(user.created_at);
  const bestStreak = Math.max(0, ...Object.values(streaks || {}));

  return (
    <div>
      <Button variant="ghost" onClick={onBack} className="mb-4 px-0">
        <ArrowLeft size={14} /> Back to all users
      </Button>

      <SectionError message={error} />

      <div className="space-y-4">
        {/* Identity */}
        <Card>
          <h4 className="font-semibold text-ink-main mb-3 flex items-center gap-2 flex-wrap break-all">
            {user.email}
            <UserBadges user={{ ...user, pro: subscription?.pro }} />
          </h4>
          <DefinitionList
            items={[
              { label: 'User ID', value: `#${user.id}` },
              {
                label: 'Joined',
                value: `${formatInstant(user.created_at, { withTime: false })}${
                  accountAge != null ? ` · ${accountAge} day${accountAge === 1 ? '' : 's'} ago` : ''
                }`,
                title: formatInstant(user.created_at),
              },
              { label: 'Auth', value: authLabel(user) },
              {
                // The fix, stated on screen. Presence is a server clock and can
                // never precede signup; the logged day is a date the user chose
                // and legitimately can.
                label: 'Last seen',
                value: detail.last_active_at ? formatRelative(detail.last_active_at) : 'Never',
                title: formatInstant(detail.last_active_at) || undefined,
              },
              {
                label: 'Last synced',
                value: detail.last_sync_at ? formatRelative(detail.last_sync_at) : 'Never synced a device',
                title: formatInstant(detail.last_sync_at) || undefined,
              },
              {
                label: 'Last habit logged',
                value: completionStats?.last_completion
                  ? `${formatCalendarDate(completionStats.last_completion)} (${formatRelativeDay(completionStats.last_completion)})`
                  : 'Never',
              },
            ]}
          />
        </Card>

        {/* Engagement */}
        <StatGrid cols={4}>
          <StatCard title="Habits" value={habits?.length || 0} icon={Target} tone="info" />
          <StatCard title="Completions" value={completionStats?.total || 0} subtitle={`${completionStats?.last_month || 0} in the last 30 days`} icon={Activity} tone="success" />
          <StatCard title="Best streak" value={bestStreak} subtitle="Consecutive days" icon={Flame} tone="warning" />
          <StatCard title="Paused weeks" value={sickWeeks?.length || 0} subtitle="Streak protection used" icon={PauseCircle} tone="neutral" />
        </StatGrid>

        <Card title="Last 90 days" icon={Activity} tone="success">
          <Sparkline data={sparkline} height={44} token={CHART_TOKENS[2]} label="Completions per day over the last 90 days" />
          <p className="text-xs text-ink-subtle mt-2">
            Counted on the habit day, so a backfilled week appears where it was logged, not when it was entered.
          </p>
        </Card>

        {/* Support — an admin had no way to see this before */}
        {support && (
          <Card title="Support" icon={LifeBuoy} tone={support.awaiting_reply ? 'warning' : 'neutral'}>
            <DefinitionList
              items={[
                { label: 'Status', value: support.status === 'open' ? 'Open' : 'Resolved' },
                { label: 'Messages', value: `${support.messages} (${support.agent_messages} from an agent)` },
                { label: 'Last message', value: support.last_message_at ? formatRelative(support.last_message_at) : '—' },
                { label: 'Opened', value: formatRelative(support.created_at) },
              ]}
            />
            {support.awaiting_reply && (
              <p className="text-sm text-warning mt-3">This thread is open and no agent has replied yet.</p>
            )}
          </Card>
        )}

        {/* Subscription */}
        {subscription && (
          <Card title="Subscription" icon={CreditCard} tone={subscription.pro ? 'warning' : 'neutral'}>
            <DefinitionList
              items={[
                { label: 'Status', value: subscription.pro ? 'Pro' : 'Free' },
                { label: 'Source', value: subscriptionSource(subscription) },
                { label: 'Store', value: subscription.pro_store || '—' },
                {
                  label: 'Expires',
                  value: subscription.pro_expires_at
                    ? `${formatInstant(subscription.pro_expires_at)} (${formatRelative(subscription.pro_expires_at)})`
                    : '—',
                },
              ]}
            />
            {subscription.events?.length > 0 && (
              <div className="mt-4">
                <h6 className="text-xs font-medium text-ink-light uppercase tracking-wide mb-1">Billing events</h6>
                <ScrollRegion desktop="sm:max-h-40" className="space-y-1">
                  {subscription.events.map((e, i) => (
                    <div key={i} className="text-xs bg-surface-input rounded px-2 py-1.5 flex justify-between gap-2">
                      <span className="font-medium text-ink-main">{e.type || 'unknown'}</span>
                      <span className="text-ink-light truncate">{e.product || ''}</span>
                      <span className="text-ink-light whitespace-nowrap" title={formatInstant(e.created_at)}>
                        {formatRelative(e.created_at)}
                      </span>
                    </div>
                  ))}
                </ScrollRegion>
              </div>
            )}
          </Card>
        )}

        {/* Devices */}
        <Card title={`Devices (${devices?.length || 0})`} icon={Smartphone} tone="info">
          {devices?.length ? (
            <ul className="space-y-2">
              {devices.map(d => (
                <li key={d.device_id} className="flex items-center justify-between gap-3 text-sm bg-surface-input rounded-lg px-3 py-2">
                  <span className="capitalize text-ink-main">{d.platform || 'Unknown platform'}</span>
                  <span className="text-ink-light text-xs whitespace-nowrap" title={formatInstant(d.last_seen_at)}>
                    {d.last_seen_at ? formatRelative(d.last_seen_at) : 'never'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>
              No devices recorded. Web clients that send no device header never register one,
              so this being empty does not mean the account is idle.
            </EmptyState>
          )}
        </Card>

        {/* Habits */}
        <Card title={`Habits (${habits?.length || 0})`} icon={Target} tone="accent">
          {habits?.length ? (
            <ul className="space-y-1">
              {habits.map(h => (
                <li key={h.id} className="text-sm bg-surface-input rounded-lg px-3 py-2 flex justify-between items-center gap-3">
                  <span className="text-ink-main truncate">{h.name}</span>
                  <span className="text-ink-light text-xs whitespace-nowrap">
                    {streaks?.[h.id] > 0 && <span className="text-warning mr-2">{streaks[h.id]}-day streak</span>}
                    Goal {h.weekly_goal}/week
                  </span>
                </li>
              ))}
            </ul>
          ) : <EmptyState>No habits yet</EmptyState>}
        </Card>

        {/* Security — never surfaced anywhere before */}
        {securityEvents?.length > 0 && (
          <Card title="Security events" icon={AlertTriangle} tone="danger">
            <ScrollRegion desktop="sm:max-h-64" className="space-y-2">
              {securityEvents.map((e, i) => (
                <div key={i} className="bg-surface-input rounded-lg px-3 py-2 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <SeverityBadge severity={e.severity} />
                    <span className="text-ink-main font-medium">{e.kind}</span>
                    {e.count > 1 && <span className="text-danger font-bold">×{e.count}</span>}
                  </div>
                  <div className="text-ink-light mt-1 flex flex-wrap gap-x-3">
                    <span className="font-mono break-all">{e.method} {e.endpoint}</span>
                    <span className="font-mono">{e.ip_address}</span>
                    <span title={formatInstant(e.last_seen)}>{formatRelative(e.last_seen)}</span>
                  </div>
                </div>
              ))}
            </ScrollRegion>
          </Card>
        )}

        {/* Recent activity */}
        {recentActivity?.length > 0 && (
          <Card title="Recent activity" icon={Activity}>
            <ScrollRegion desktop="sm:max-h-40" className="space-y-1">
              {recentActivity.map((a, i) => (
                <div key={i} className="text-xs bg-surface-input rounded px-2 py-1.5 flex justify-between gap-2">
                  <span className="font-medium text-ink-main capitalize">{a.action.replace(/_/g, ' ')}</span>
                  <span className="text-ink-light whitespace-nowrap" title={formatInstant(a.created_at)}>
                    {formatRelative(a.created_at)}
                  </span>
                </div>
              ))}
            </ScrollRegion>
          </Card>
        )}

        {/* Actions */}
        <Card title="Account actions" icon={Shield} tone="warning">
          <FlagToggle
            label="Support agent"
            description="Can read and reply to support threads."
            on={!!user.support_agent}
            onChange={toggleSupportAgent}
            busy={busy}
          />
          <FlagToggle
            label="Admin"
            description="Full access to this dashboard."
            on={!!user.is_admin}
            onChange={(v) => toggleFlag('is_admin', v)}
            busy={busy}
          />
          <FlagToggle
            label="Email verified"
            description="Bypasses the confirmation step."
            on={!!user.email_verified}
            onChange={(v) => toggleFlag('email_verified', v)}
            busy={busy}
          />
          <FlagToggle
            label="Pro override"
            description="Grants Pro independently of the store. A billing webhook cannot revoke it."
            on={!!user.pro_override}
            onChange={(v) => toggleFlag('pro_override', v)}
            busy={busy}
          />
          <FlagToggle
            label="Daily overview email"
            description="Receives the 8am business summary. Independent of admin, so a stakeholder can get the numbers without gaining access to this dashboard."
            on={!!user.daily_overview_email}
            onChange={(v) => toggleFlag('daily_overview_email', v)}
            busy={busy}
          />

          <div className="flex flex-wrap gap-2 pt-4 mt-2 border-t border-divider">
            <Button variant={confirmingDelete ? 'dangerSolid' : 'danger'} onClick={deleteUser} disabled={busy}>
              {busy && confirmingDelete ? 'Deleting…' : confirmingDelete ? 'Really delete this account?' : 'Delete account'}
            </Button>
            {confirmingDelete && !busy && (
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
            )}
          </div>
          {confirmingDelete && (
            <p className="text-xs text-ink-light mt-2">
              Permanent. Habits, completions and support history go with it; an anonymous
              row is kept for churn stats.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
